import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  UserPlus,
  ShieldCheck,
  KeyRound,
  Activity,
  Power,
  Edit3,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

export const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ active_operational_users: 0, max_active_operational_users: 2, total_users: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [createForm, setCreateForm] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    phone: '',
    role: 'USER',
    status: 'ACTIVE'
  });
  const [editForm, setEditForm] = useState({ name: '', username: '', phone: '', email: '' });
  const [newPassword, setNewPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [userActivities, setUserActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/users');
      if (res.data?.success) {
        setUsers(res.data.data || []);
        if (res.data.meta) {
          setMeta(res.data.meta);
        }
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to load user directory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const isLimitReached = meta.active_operational_users >= meta.max_active_operational_users;

  // Filtered users
  const filteredUsers = users.filter((u) => {
    const q = searchTerm.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  });

  // Handle Create User
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        name: createForm.name ? createForm.name.trim() : '',
        username: createForm.username ? createForm.username.trim().toLowerCase() : '',
        password: createForm.password,
        role: createForm.role || 'USER',
        status: createForm.status || 'ACTIVE',
        email: createForm.email && createForm.email.trim() ? createForm.email.trim().toLowerCase() : null,
        phone: createForm.phone && createForm.phone.trim() ? createForm.phone.trim() : null,
      };
      const res = await api.post('/api/users', payload);
      if (res.data?.success) {
        toast.success('User account created successfully');
        setCreateModalOpen(false);
        setCreateForm({ name: '', username: '', email: '', password: '', phone: '', role: 'USER', status: 'ACTIVE' });
        fetchUsers();
      }
    } catch (err) {
      const detail = err.response?.data?.message || err.response?.data?.detail || 'Failed to create user';
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Edit User
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const payload = {
        name: editForm.name ? editForm.name.trim() : undefined,
        username: editForm.username ? editForm.username.trim().toLowerCase() : undefined,
        email: editForm.email && editForm.email.trim() ? editForm.email.trim().toLowerCase() : null,
        phone: editForm.phone && editForm.phone.trim() ? editForm.phone.trim() : null,
      };
      const res = await api.put(`/api/users/${selectedUser.id}`, payload);
      if (res.data?.success) {
        toast.success('User details updated');
        setEditModalOpen(false);
        fetchUsers();
      }
    } catch (err) {
      const detail = err.response?.data?.message || err.response?.data?.detail || 'Failed to update user';
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Toggle Status (Active / Inactive)
  const handleToggleStatus = async (user) => {
    const nextStatus = user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      const res = await api.patch(`/api/users/${user.id}/status`, { status: nextStatus });
      if (res.data?.success) {
        toast.success(res.data.message || `User status changed to ${nextStatus}`);
        fetchUsers();
      }
    } catch (err) {
      const detail = err.response?.data?.message || err.response?.data?.detail || 'Failed to change status';
      toast.error(detail);
    }
  };

  // Handle Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/api/users/${selectedUser.id}/reset-password`, { new_password: newPassword });
      if (res.data?.success) {
        toast.success(`Password for ${selectedUser.name} reset successfully`);
        setResetModalOpen(false);
        setNewPassword('');
      }
    } catch (err) {
      const detail = err.response?.data?.message || err.response?.data?.detail || 'Failed to reset password';
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  // View Activity
  const openActivityModal = async (user) => {
    setSelectedUser(user);
    setActivityModalOpen(true);
    setActivityLoading(true);
    try {
      const res = await api.get(`/api/users/${user.id}/activity`);
      if (res.data?.success) {
        setUserActivities(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load user activity', err);
      toast.error('Failed to load activity logs');
    } finally {
      setActivityLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-start justify-between gap-3 w-full sm:w-auto">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">User Management</h1>
            <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1">
              Manage operational staff accounts, permissions, and prescription workload.
            </p>
          </div>
          {/* Mobile Icon-only Refresh Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={fetchUsers}
            disabled={loading}
            className="sm:hidden h-9 w-9 flex-shrink-0"
            aria-label="Refresh users"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Desktop Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchUsers}
            disabled={loading}
            className="hidden sm:inline-flex h-9 items-center justify-center gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => {
              // Pre-fill default status based on limit
              setCreateForm(prev => ({ ...prev, status: isLimitReached ? 'INACTIVE' : 'ACTIVE' }));
              setCreateModalOpen(true);
            }}
            className="w-full sm:w-auto h-10 sm:h-9 flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium"
          >
            <UserPlus className="h-4 w-4" />
            Add Staff User
          </Button>
        </div>
      </div>

      {/* Operational Seat Limit Metric Cards (2-column on mobile) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-4">
        <Card className="border-border shadow-sm min-w-0">
          <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
            <CardDescription className="text-[10px] sm:text-xs uppercase font-medium tracking-wider truncate">
              Operational Seats
            </CardDescription>
            <div className="flex items-center justify-between gap-1 mt-1">
              <CardTitle className="text-lg sm:text-2xl font-bold font-serif">
                {meta.active_operational_users} / {meta.max_active_operational_users}
              </CardTitle>
              <Badge variant={isLimitReached ? "destructive" : "secondary"} className="text-[10px] sm:text-xs px-1.5 py-0.5 whitespace-nowrap">
                {isLimitReached ? "Full" : `${meta.max_active_operational_users - meta.active_operational_users} Avail`}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-3 pt-0 text-[11px] text-muted-foreground line-clamp-2">
            Max active staff quota: 2 seats.
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm min-w-0">
          <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
            <CardDescription className="text-[10px] sm:text-xs uppercase font-medium tracking-wider truncate">
              Total Accounts
            </CardDescription>
            <CardTitle className="text-lg sm:text-2xl font-bold font-serif mt-1">
              {meta.total_users} Users
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-3 pt-0 text-[11px] text-muted-foreground line-clamp-2">
            Includes admins & inactive staff.
          </CardContent>
        </Card>

        <Card className={`hidden sm:block border shadow-sm min-w-0 ${isLimitReached ? 'border-amber-200 bg-amber-50/40 dark:bg-amber-950/20' : 'border-border'}`}>
          <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
            <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <CardTitle className="text-xs sm:text-sm font-semibold truncate">Seat Policy</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-3 pt-0 text-[11px] text-muted-foreground">
            Deactivate a user to free a seat.
          </CardContent>
        </Card>
      </div>

      {/* User Directory Table Card */}
      <Card className="border-border shadow-sm">
        <CardHeader className="border-b border-border/60 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-lg font-serif">Staff Directory</CardTitle>
              <CardDescription className="text-xs">
                Active and inactive users eligible for prescription fulfillment
              </CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search name, username, or email..."
                className="pl-9 h-11 sm:h-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* ── MOBILE card list (< md) ── */}
          <div className="md:hidden divide-y divide-border/50">
            {loading ? (
              <div className="py-10 flex items-center justify-center text-muted-foreground">
                <RefreshCw className="h-5 w-5 animate-spin text-primary mr-2" />
                Loading...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">
                No users found.
              </div>
            ) : filteredUsers.map((u) => {
              const isAdmin = u.role === 'ADMIN';
              const isActive = u.status === 'ACTIVE';
              return (
                <div key={u.id} className="p-3.5 sm:p-4 flex flex-col gap-3">
                  {/* User info row */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-sm break-words">{u.name}</span>
                        {isAdmin && <ShieldCheck className="h-3.5 w-3.5 text-primary flex-shrink-0" />}
                        <Badge variant={isAdmin ? 'default' : 'outline'} className="text-[10px] font-normal h-4 px-1.5">{u.role}</Badge>
                      </div>
                      <p className="text-xs font-mono text-primary font-medium mt-0.5">@{u.username || u.email}</p>
                      {u.email && <p className="text-xs text-muted-foreground break-all">{u.email}</p>}
                      {u.phone && <p className="text-xs text-muted-foreground mt-0.5">{u.phone}</p>}
                    </div>
                    <Badge
                      variant={isActive ? 'secondary' : 'destructive'}
                      className={`text-xs flex-shrink-0 ${isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}`}
                    >
                      {isActive ? 'ACTIVE' : 'INACTIVE'}
                    </Badge>
                  </div>

                  {/* Stats row */}
                  {!isAdmin && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground bg-muted/30 p-2 rounded-md">
                      <span>Assigned: <strong className="text-foreground">{u.assigned_count || 0}</strong></span>
                      <span>Completed: <strong className="text-emerald-700">{u.completed_count || 0}</strong></span>
                      <span>Last login: {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</span>
                    </div>
                  )}

                  {/* Action buttons in 2x2 grid for mobile */}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 text-xs gap-1.5 justify-center"
                      onClick={() => { setSelectedUser(u); setEditForm({ name: u.name, username: u.username || '', phone: u.phone || '', email: u.email || '' }); setEditModalOpen(true); }}
                    >
                      <Edit3 className="h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 text-xs gap-1.5 justify-center"
                      onClick={() => { setSelectedUser(u); setNewPassword(''); setResetModalOpen(true); }}
                    >
                      <KeyRound className="h-3.5 w-3.5" /> Password
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 text-xs gap-1.5 justify-center"
                      onClick={() => openActivityModal(u)}
                    >
                      <Activity className="h-3.5 w-3.5" /> Activity
                    </Button>
                    {!isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className={`h-9 text-xs gap-1.5 justify-center ${isActive ? 'border-destructive text-destructive hover:bg-destructive/10' : 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'}`}
                        onClick={() => handleToggleStatus(u)}
                      >
                        <Power className="h-3.5 w-3.5" />
                        {isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── DESKTOP table (md+) ── */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User / Staff Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Assigned Active</TableHead>
                <TableHead className="text-center">Completed Work</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="h-6 w-6 animate-spin text-primary" />
                      <span>Loading user directory...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                    No users found matching your search.
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((u) => {
                  const isAdmin = u.role === 'ADMIN';
                  const isActive = u.status === 'ACTIVE';

                  return (
                    <TableRow key={u.id} className="hover:bg-muted/40">
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-semibold text-foreground flex items-center gap-1.5">
                            {u.name}
                            {isAdmin && (
                              <span title="Administrator">
                                <ShieldCheck className="h-4 w-4 text-primary" />
                              </span>
                            )}
                          </span>
                          <span className="text-xs font-mono text-primary font-medium">@{u.username || u.email}</span>
                          {u.email && <span className="text-xs text-muted-foreground">{u.email}</span>}
                          {u.phone && <span className="text-xs text-muted-foreground">{u.phone}</span>}
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge variant={isAdmin ? "default" : "outline"} className="text-xs font-normal">
                          {u.role}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <Badge
                          variant={isActive ? "secondary" : "destructive"}
                          className={`text-xs ${isActive ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-200' : ''}`}
                        >
                          {isActive ? (
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                              ACTIVE
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <XCircle className="h-3 w-3" />
                              INACTIVE
                            </span>
                          )}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-center font-medium">
                        {isAdmin ? '—' : (
                          <Badge variant="outline" className={u.assigned_count > 0 ? "bg-blue-50 text-blue-700 border-blue-200" : ""}>
                            {u.assigned_count || 0}
                          </Badge>
                        )}
                      </TableCell>

                      <TableCell className="text-center font-medium">
                        {isAdmin ? '—' : (
                          <Badge variant="outline" className={u.completed_count > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : ""}>
                            {u.completed_count || 0}
                          </Badge>
                        )}
                      </TableCell>

                      <TableCell className="text-xs text-muted-foreground">
                        {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Edit User Button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Edit User Info"
                            onClick={() => {
                              setSelectedUser(u);
                              setEditForm({ name: u.name, username: u.username || '', phone: u.phone || '', email: u.email || '' });
                              setEditModalOpen(true);
                            }}
                          >
                            <Edit3 className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          </Button>

                          {/* Reset Password Button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Reset Password"
                            onClick={() => {
                              setSelectedUser(u);
                              setNewPassword('');
                              setResetModalOpen(true);
                            }}
                          >
                            <KeyRound className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          </Button>

                          {/* Activity Logs Button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            title="View Activity"
                            onClick={() => openActivityModal(u)}
                          >
                            <Activity className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          </Button>

                          {/* Toggle Active / Inactive Status (Not for own admin) */}
                          {!isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={isActive ? "Deactivate User" : "Activate User"}
                              onClick={() => handleToggleStatus(u)}
                              className={isActive ? "hover:text-destructive" : "hover:text-emerald-600"}
                            >
                              <Power className={`h-4 w-4 ${isActive ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* CREATE USER MODAL */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Create Staff User</DialogTitle>
            <DialogDescription className="text-xs">
              Add a new staff member to process prescriptions and manage fulfillments.
            </DialogDescription>
          </DialogHeader>

          {isLimitReached && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
              <span>
                <strong>Active limit reached:</strong> 2 active operational staff seats are already occupied. You can create this user as <em>INACTIVE</em> now and activate later.
              </span>
            </div>
          )}

          <form onSubmit={handleCreateSubmit} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="create-name">Full Name *</Label>
              <Input
                id="create-name"
                required
                placeholder="e.g. Ramesh Chandra"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-username">Username *</Label>
              <Input
                id="create-username"
                required
                placeholder="e.g. ramesh_c"
                value={createForm.username}
                onChange={(e) => setCreateForm({ ...createForm, username: e.target.value.toLowerCase().trim() })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-email">Email Address (optional)</Label>
              <Input
                id="create-email"
                type="email"
                placeholder="e.g. ramesh@clinic.com"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-password">Initial Password * (min 6 characters)</Label>
              <Input
                id="create-password"
                type="password"
                required
                minLength={6}
                placeholder="Choose strong password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-phone">Phone Number (optional)</Label>
              <Input
                id="create-phone"
                placeholder="+91 9876543210"
                value={createForm.phone}
                onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="create-role">Role</Label>
                <select
                  id="create-role"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                >
                  <option value="USER">USER (Staff)</option>
                  <option value="ADMIN">ADMIN (Doctor)</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="create-status">Status</Label>
                <select
                  id="create-status"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={createForm.status}
                  onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}
                >
                  <option value="ACTIVE" disabled={isLimitReached && createForm.role === 'USER'}>
                    ACTIVE {isLimitReached && createForm.role === 'USER' ? '(Limit reached)' : ''}
                  </option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
            </div>

            <DialogFooter className="pt-4 flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="outline" className="h-11 sm:h-10" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading} className="h-11 sm:h-10">
                {actionLoading ? 'Creating...' : 'Create Account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* EDIT USER MODAL */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Edit User Information</DialogTitle>
            <DialogDescription className="text-xs">
              Update personal details for {selectedUser?.name}.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditSubmit} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-name">Full Name *</Label>
              <Input
                id="edit-name"
                required
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-username">Username *</Label>
              <Input
                id="edit-username"
                required
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value.toLowerCase().trim() })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-email">Email Address (optional)</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-phone">Phone Number</Label>
              <Input
                id="edit-phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>

            <DialogFooter className="pt-4 flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="outline" className="h-11 sm:h-10" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading} className="h-11 sm:h-10">
                {actionLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* RESET PASSWORD MODAL */}
      <Dialog open={resetModalOpen} onOpenChange={setResetModalOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Reset User Password</DialogTitle>
            <DialogDescription className="text-xs">
              Set a new secure password for <strong>{selectedUser?.name}</strong> ({selectedUser?.email}).
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleResetPassword} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="reset-new-password">New Password (min 6 characters) *</Label>
              <Input
                id="reset-new-password"
                type="password"
                required
                minLength={6}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>

            <DialogFooter className="pt-4 flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="outline" className="h-11 sm:h-10" onClick={() => setResetModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading} className="h-11 sm:h-10">
                {actionLoading ? 'Resetting...' : 'Update Password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* USER ACTIVITY MODAL */}
      <Dialog open={activityModalOpen} onOpenChange={setActivityModalOpen}>
        <DialogContent className="sm:max-w-xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Activity Ledger: {selectedUser?.name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Audit timeline of prescription actions and status transitions performed by this staff member.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2 space-y-3 py-2">
            {activityLoading ? (
              <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
                <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                Loading activity history...
              </div>
            ) : userActivities.length === 0 ? (
              <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
                No recorded prescription activities for this user yet.
              </div>
            ) : (
              userActivities.map((act) => (
                <div key={act.id} className="p-3 rounded-lg border border-border/70 bg-card text-xs space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-foreground flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      {act.action.replace('_', ' ')}
                    </span>
                    <span className="text-muted-foreground">
                      {new Date(act.created_at).toLocaleString()}
                    </span>
                  </div>
                  {act.metadata_text && (
                    <p className="text-muted-foreground bg-muted/30 p-2 rounded text-xs font-mono">
                      {act.metadata_text}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span>Prescription #{act.prescription_id}</span>
                    {act.previous_status && act.new_status && (
                      <span>({act.previous_status} → {act.new_status})</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="h-11 sm:h-10" onClick={() => setActivityModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
